'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { type CheckboxProps as RadixCheckboxProps } from '@radix-ui/react-checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import { useSubscribedState } from '../../../hooks';
const checkboxVariants = cva(['rounded-xxsmall', 'm-2 size-16 min-h-16 min-w-16', 'flex items-center justify-center', 'transition-colors duration-200 ease-in-out'], {
  variants: {
    checked: {
      false: ['bg-white', 'border border-Gray-300', 'bg-Gray-white hover:border-Gray-500'],
      true: ['bg-Gray-black', 'border-none']
    },
    disabled: {
      true: ['bg-Gray-300', 'cursor-not-allowed'],
      false: []
    }
  },
  defaultVariants: {
    checked: false,
    disabled: false
  }
});
interface CheckboxProps extends Omit<RadixCheckboxProps, 'checked' | 'disabled' | 'onCheckedChange'>, VariantProps<typeof checkboxVariants> {
  checked?: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}
const Checkbox = t0 => {
  const $ = _c(27);
  let checked;
  let className;
  let onCheckedChange;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      checked,
      disabled: t1,
      onCheckedChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = className;
    $[3] = onCheckedChange;
    $[4] = props;
    $[5] = t1;
  } else {
    checked = $[1];
    className = $[2];
    onCheckedChange = $[3];
    props = $[4];
    t1 = $[5];
  }
  const disabled = t1 === undefined ? false : t1;
  const [turned, setTurned] = useSubscribedState(checked ?? false);
  const isInnerHandled = typeof checked !== "boolean";
  let t2;
  if ($[6] !== className) {
    t2 = cn("flex items-start gap-4 p-4", "cursor-pointer whitespace-pre-wrap break-keep font-400 typo-body4", "text-Gray-900", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = isInnerHandled ? turned : checked;
  let t4;
  if ($[8] !== disabled || $[9] !== turned) {
    t4 = cn(checkboxVariants({
      checked: turned,
      disabled
    }));
    $[8] = disabled;
    $[9] = turned;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isInnerHandled || $[12] !== onCheckedChange || $[13] !== setTurned) {
    t5 = state => {
      const checked_0 = state.valueOf();
      if (typeof checked_0 !== "boolean") {
        throw new Error("checked is not boolean");
      }
      onCheckedChange?.(checked_0);
      if (isInnerHandled) {
        setTurned(checked_0);
      }
    };
    $[11] = isInnerHandled;
    $[12] = onCheckedChange;
    $[13] = setTurned;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn("flex transition-opacity duration-150 motion-reduce:transition-none", "data-[state=checked]:opacity-100 data-[state=unchecked]:opacity-0");
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <RadixCheckbox.Indicator forceMount={true} className={t6}><svg fill="none" height="8" viewBox="0 0 10 8" width="10" xmlns="http://www.w3.org/2000/svg"><path d="M8.84998 0.0342066L8.96728 0.00623386C8.99038 0.000697772 9.08635 -0.00158285 9.18053 0.0011657C9.32616 0.00541523 9.37324 0.0159804 9.49551 0.0718481C9.93989 0.274909 10.124 0.753625 9.91226 1.15538C9.88152 1.21374 8.65774 2.64989 7.03759 4.52896C4.76204 7.16814 4.19664 7.81288 4.11399 7.86276C3.85282 8.02044 3.52669 8.04387 3.25337 7.92461C3.12171 7.86717 0.306588 5.90129 0.212848 5.80133C0.0507154 5.62844 -0.0321884 5.35453 0.0116154 5.13651C0.112599 4.63421 0.678898 4.33469 1.18835 4.51415C1.2721 4.54364 1.6097 4.76721 2.38382 5.30575C2.97717 5.71854 3.46561 6.05626 3.46926 6.05626C3.47291 6.05626 4.6103 4.74004 5.99677 3.1313C8.29616 0.463312 8.52891 0.199568 8.64615 0.129237C8.71683 0.0868192 8.80855 0.0440702 8.84998 0.0342066Z" fill="white" /></svg></RadixCheckbox.Indicator>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== disabled || $[18] !== props || $[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
    t8 = <RadixCheckbox.Root checked={t3} className={t4} disabled={disabled} onCheckedChange={t5} {...props}>{t7}</RadixCheckbox.Root>;
    $[17] = disabled;
    $[18] = props;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== props.children || $[24] !== t2 || $[25] !== t8) {
    t9 = <label className={t2}>{t8}{props.children}</label>;
    $[23] = props.children;
    $[24] = t2;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};
export { Checkbox, type CheckboxProps };